import { useState, useCallback } from 'react';
import { 
  getSiteMedia, 
  addSiteMedia, 
  deleteSiteMedia, 
  setMainSiteMedia,
  updateMediaFeatures,
  updateMediaSubLocation
} from '../firebase/siteMedia';

export function useMedia(siteId) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [media, setMedia] = useState([]);

  // Load all media for a site
  const loadMedia = useCallback(async (sid = siteId) => {
    setLoading(true);
    setError(null);
    
    try {
      if (!sid) {
        console.log('loadMedia: No site ID provided');
        setError('No site ID provided');
        return { success: false, error: 'No site ID provided', data: [] };
      }

      const result = await getSiteMedia(sid);
      if (result.success) {
        // Add siteId to each media item
        const mediaWithSiteId = result.data.map(item => ({
          ...item,
          siteId: sid
        }));
        setMedia(mediaWithSiteId);
        console.log('loadMedia success:', {mediaCount: mediaWithSiteId.length});
        return { success: true, data: mediaWithSiteId };
      } else {
        console.log('loadMedia error from getSiteMedia:', result.error);
        setError(result.error);
        return { success: false, error: result.error, data: [] };
      }
    } catch (err) {
      const error = 'Failed to load media';
      console.error('Error in loadMedia:', err);
      setError(error);
      return { success: false, error, data: [] };
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  // Add new media
  const uploadMedia = useCallback(async (mediaData) => {
    if (!siteId) return;
    
    setError(null);
    
    try {
      const result = await addSiteMedia(siteId, mediaData);
      if (result.success) {
        // Reload media to get the updated list
        await loadMedia();
        return result;
      } else {
        setError(result.error);
        return result;
      }
    } catch (err) {
      const error = 'Failed to upload media';
      setError(error);
      console.error('Error in uploadMedia:', err);
      return { success: false, error };
    }
  }, [siteId, loadMedia]);

  // Delete media
  const removeMedia = useCallback(async (mediaId) => {
    if (!siteId) return;
    
    setError(null);
    
    try {
      const result = await deleteSiteMedia(siteId, mediaId);
      if (result.success) {
        // Update local state by removing the deleted media
        setMedia(prevMedia => prevMedia.filter(item => item.id !== mediaId));
        return result;
      } else {
        setError(result.error);
        return result;
      }
    } catch (err) {
      const error = 'Failed to delete media';
      setError(error);
      console.error('Error in removeMedia:', err);
      return { success: false, error };
    }
  }, [siteId]);

  // Set main media
  const setMainMedia = useCallback(async (mediaId) => {
    if (!siteId) return;
    
    setError(null);
    
    try {
      const result = await setMainSiteMedia(siteId, mediaId);
      if (result.success) {
        // Update local state to reflect the new main media
        setMedia(prevMedia => prevMedia.map(item => ({
          ...item,
          siteId,
          isMain: item.id === mediaId
        })));
        return result;
      } else {
        setError(result.error);
        return result;
      }
    } catch (err) {
      const error = 'Failed to set main media';
      setError(error);
      console.error('Error in setMainMedia:', err);
      return { success: false, error };
    }
  }, [siteId]);

  // Utility function to get main media
  const getMainMedia = useCallback(() => {
    return media.find(item => item.isMain) || media[0];
  }, [media]);

  // Update media features
  const updateFeatures = useCallback(async (mediaId, features) => {
    if (!siteId) return;
    
    setError(null);
    
    try {
      const result = await updateMediaFeatures(siteId, mediaId, features);
      if (result.success) {
        // Reload media to get the updated features
        await loadMedia();
        return result;
      } else {
        setError(result.error);
        return result;
      }
    } catch (err) {
      const error = 'Failed to update media features';
      setError(error);
      console.error('Error in updateFeatures:', err);
      return { success: false, error };
    }
  }, [siteId, loadMedia]);

  return {
    media,
    loading,
    error,
    loadMedia,
    uploadMedia,
    removeMedia,
    setMainMedia,
    getMainMedia,
    updateFeatures,
    updateMedia: useCallback(async (mediaId, updates) => {
      if (!siteId) return { success: false, error: 'No site ID provided' };
      
      setError(null);
      
      try {
        const result = await updateMediaSubLocation(siteId, mediaId, updates);
        if (result.success) {
          // Update local state to reflect changes
          setMedia(prevMedia => prevMedia.map(item => 
            item.id === mediaId 
              ? { ...item, ...updates, siteId }
              : item
          ));
        } else {
          setError(result.error);
        }
        return result;
      } catch (err) {
        const error = 'Failed to update media';
        setError(error);
        console.error('Error in updateMedia:', err);
        return { success: false, error };
      }
    }, [siteId])
  };
}
